import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AdminApiService} from '../../admin-api.service';
import {AuditEvent} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {auditActionLabel} from '../../shared/audit-action-labels';
import {UiButtonComponent, UiIconComponent, UiInputComponent, UiSelectComponent, UiToastService} from 'freyr-ui';

@Component({
  selector: 'app-audit-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, UiButtonComponent, UiInputComponent, UiSelectComponent, UiIconComponent, AdminTranslatePipe],
  templateUrl: './audit-page.component.html',
  styleUrl: './audit-page.component.scss'
})
export class AuditPageComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);

  auditEvents: AuditEvent[] = [];
  auditTotal = 0;
  loadingAudit = false;
  filters = {
    query: '',
    actorUserId: '',
    effectiveUserId: '',
    supportSessionId: '',
    actionType: '',
    requestId: '',
    success: ''
  };

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.filters = {
        query: params.get('query') ?? '',
        actorUserId: params.get('actorUserId') ?? '',
        effectiveUserId: params.get('effectiveUserId') ?? '',
        supportSessionId: params.get('supportSessionId') ?? '',
        actionType: params.get('actionType') ?? '',
        requestId: params.get('requestId') ?? '',
        success: params.get('success') ?? ''
      };
      this.loadAuditEvents();
    });
  }

  applyFilters(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.filters.query || null,
        actorUserId: this.filters.actorUserId || null,
        effectiveUserId: this.filters.effectiveUserId || null,
        supportSessionId: this.filters.supportSessionId || null,
        actionType: this.filters.actionType || null,
        requestId: this.filters.requestId || null,
        success: this.filters.success || null
      }
    });
  }

  resetFilters(): void {
    this.filters = {
      query: '',
      actorUserId: '',
      effectiveUserId: '',
      supportSessionId: '',
      actionType: '',
      requestId: '',
      success: ''
    };
    this.applyFilters();
  }

  loadAuditEvents(): void {
    this.loadingAudit = true;
    this.api.listAuditEvents(this.filters).subscribe({
      next: (page) => {
        const events = Array.isArray(page?.content) ? page.content : [];
        const total = page?.totalElements ?? events.length;

        if (this.shouldFallbackToSessionQuery(events)) {
          this.loadAuditEventsBySessionQuery(this.filters.supportSessionId);
          return;
        }

        this.auditEvents = events;
        this.auditTotal = total;
        this.loadingAudit = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loadingAudit = false;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.audit.loadError');
        this.toast.error(message);
      }
    });
  }

  statusLabel(event: AuditEvent): string {
    return event.success
      ? this.i18n.t('admin.common.status.success')
      : this.i18n.t('admin.common.status.error');
  }

  formatOccurredAt(value: string): string {
    return this.i18n.t('admin.audit.at', {
      date: new Date(value).toLocaleString(this.i18n.currentLang === 'en' ? 'en-GB' : 'fr-FR')
    });
  }

  actionLabel(actionType: string): string {
    return auditActionLabel(actionType, this.i18n.currentLang);
  }

  private shouldFallbackToSessionQuery(events: AuditEvent[]): boolean {
    return !!this.filters.supportSessionId?.trim()
      && !this.filters.query?.trim()
      && !this.filters.actorUserId?.trim()
      && !this.filters.effectiveUserId?.trim()
      && !this.filters.actionType?.trim()
      && !this.filters.requestId?.trim()
      && !events.length;
  }

  private loadAuditEventsBySessionQuery(sessionId: string): void {
    this.api.listAuditEvents({
      query: sessionId,
      success: this.filters.success
    }).subscribe({
      next: (page) => {
        this.auditEvents = Array.isArray(page?.content) ? page.content : [];
        this.auditTotal = page?.totalElements ?? this.auditEvents.length;
        this.loadingAudit = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loadingAudit = false;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.audit.loadError');
        this.toast.error(message);
      }
    });
  }
}

